import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import {
  Trophy,
  Plus,
  Pencil,
  Loader2,
  AlertCircle,
  ArrowLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Sparkles,
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

export default function IncentiveTypesPage() {
  const navigate = useNavigate();
  const { isAdmin, isSeniorScheduler } = useAuth();
  const { toast } = useToast();

  const canManage = isAdmin || isSeniorScheduler;

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seeding, setSeeding] = useState(false);

  // Edit/Create dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/incentives/types', { params: { include_inactive: true } });
      setTypes(res.data);
    } catch (err) {
      setError(getApiError(err, 'Failed to load incentive types.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/incentives/types/seed-defaults');
      toast({ title: 'Defaults created', description: res.data.message });
      await fetchTypes();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to seed defaults.'), variant: 'destructive' });
    } finally {
      setSeeding(false);
    }
  };

  const openCreate = () => {
    setEditingType(null);
    setForm({ name: '', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditingType(t);
    setForm({ name: t.name, description: t.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Validation', description: 'Name is required.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editingType) {
        await api.patch(`/incentives/types/${editingType.id}`, {
          name: form.name.trim(),
          description: form.description.trim() || null,
        });
        toast({ title: 'Updated', description: `"${form.name.trim()}" updated.` });
      } else {
        await api.post('/incentives/types', {
          name: form.name.trim(),
          description: form.description.trim() || null,
        });
        toast({ title: 'Created', description: `"${form.name.trim()}" created.` });
      }
      setDialogOpen(false);
      await fetchTypes();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save.'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (t) => {
    try {
      await api.patch(`/incentives/types/${t.id}`, { is_active: !t.is_active });
      toast({
        title: t.is_active ? 'Deactivated' : 'Activated',
        description: `"${t.name}" is now ${t.is_active ? 'inactive' : 'active'}.`,
      });
      await fetchTypes();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to update.'), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="incentive-types-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/incentives" className="hover:text-slate-700 flex items-center gap-1">
          <Trophy className="h-4 w-4" /> Incentives
        </Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-slate-700 font-medium">Incentive Types</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/incentives')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Incentive Types</h1>
            <p className="text-sm text-slate-500 mt-0.5">Create and manage incentive programme types</p>
          </div>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="gap-2" data-testid="create-type-btn">
            <Plus className="h-4 w-4" /> New Type
          </Button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <p className="text-sm text-slate-600">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchTypes}>Retry</Button>
          </CardContent>
        </Card>
      ) : types.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="h-16 w-16 rounded-full bg-amber-50 flex items-center justify-center">
              <Trophy className="h-8 w-8 text-amber-400" />
            </div>
            <div>
              <p className="font-medium text-slate-700">No incentive types configured</p>
              <p className="text-sm text-slate-500 mt-1">
                Create incentive types to get started, or seed the defaults.
              </p>
            </div>
            {canManage && (
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={handleSeedDefaults} disabled={seeding} className="gap-2" data-testid="seed-defaults-btn">
                  {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  Seed Defaults (Auditor & Leadership)
                </Button>
                <Button onClick={openCreate} className="gap-2" data-testid="empty-create-btn">
                  <Plus className="h-4 w-4" /> Create Custom
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {types.map((t) => (
            <Card key={t.id} className="hover:border-slate-300 transition-all" data-testid={`type-row-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${t.is_active ? 'bg-amber-100' : 'bg-slate-100'}`}>
                      <Trophy className={`h-4 w-4 ${t.is_active ? 'text-amber-600' : 'text-slate-400'}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium text-sm ${t.is_active ? 'text-slate-900' : 'text-slate-400'}`}>
                          {t.name}
                        </span>
                        {t.is_active ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1 text-xs">
                            <CheckCircle2 className="h-2.5 w-2.5" /> Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-400 border-slate-200 gap-1 text-xs">
                            <XCircle className="h-2.5 w-2.5" /> Inactive
                          </Badge>
                        )}
                      </div>
                      {t.description && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{t.description}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-0.5">
                        Created by {t.created_by_name || '—'}
                        {t.created_at && <> · {new Date(t.created_at).toLocaleDateString('en-GB')}</>}
                      </p>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{t.is_active ? 'Active' : 'Inactive'}</span>
                        <Switch
                          checked={t.is_active}
                          onCheckedChange={() => handleToggleActive(t)}
                          data-testid={`toggle-active-${t.id}`}
                        />
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-type-${t.id}`}>
                        <Pencil className="h-4 w-4 text-slate-400" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Incentive Type' : 'New Incentive Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name <span className="text-red-500">*</span></Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Auditor Bonus"
                autoFocus
                data-testid="type-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
                placeholder="Brief description of this incentive programme…"
                data-testid="type-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.name.trim()} data-testid="save-type-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingType ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
